import "./CourseGradeCard.css";

import { round } from "lodash-es";
import { type FC, useId } from "react";
import { twMerge } from "tailwind-merge";

import { calculateRubricScore } from "components/materials/course/assessment/hooks";
import { COURSE_SETTINGS_GRADE_TAB_INDEX } from "components/modals/CourseSettingsModal";
import { CourseScoreDisplayFormat, CourseType } from "components/server-types";
import { t } from "i18n/i18n";
import { Button } from "mds/components/Button";
import { IconText } from "mds/components/IconText";
import { Tooltip } from "mds/components/Tooltip";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { CircleInfoIcon, GearSettingsIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { useAppDispatch, useAppSelector } from "store/index";
import {
  selectAverageCourseGradeForGradeCategory,
  selectCourseUserIdByUserId,
  selectCurrentCourse,
  selectCurrentOrg,
  selectCurrentUserId,
  selectUserById,
} from "store/selectors";
import type { GradeCategory } from "store/selectors/types";
import { viewStateActions } from "store/slices/view";
import { getUserFullName } from "utils/user-utils";

type CourseGradeCardSectionProps = {
  className?: string;
  title: string;
  maxValue: number;
  currentValue?: number;
  isCourseScore?: boolean;
  userId?: string | null;
};

const CourseGradeCardSection: FC<CourseGradeCardSectionProps> = ({
  className,
  title,
  maxValue,
  currentValue,
  isCourseScore,
  userId,
}) => {
  const currentOrg = useAppSelector(selectCurrentOrg);
  const { canAuthorCourse } = useCapabilities();
  const id = useId();

  const courseScoreDisplayFormat =
    currentOrg?.course_score_display_format || CourseScoreDisplayFormat.Percent;
  const shouldShowPercent =
    !isCourseScore || courseScoreDisplayFormat === CourseScoreDisplayFormat.Percent;

  return (
    <div className={twMerge("flex min-w-1 shrink flex-col gap-2", className)}>
      <div className="flex min-w-1 shrink flex-row items-center justify-between gap-1">
        <label className="min-w-1 shrink truncate text-sm font-normal" htmlFor={id}>
          {title}
        </label>{" "}
        {currentValue === undefined ? (
          <div className="shrink-0 text-sm font-semibold text-blue">
            {maxValue}
            {shouldShowPercent ? "%" : undefined}
          </div>
        ) : (
          <Tooltip
            text={t(
              `assessment_dashboard.tooltip.${canAuthorCourse && !userId ? "instructor" : "student"}.${isCourseScore ? courseScoreDisplayFormat : "category"}`,
            )}
          >
            <div className="flex shrink-0 items-baseline gap-1 whitespace-nowrap">
              <span className="text-sm font-semibold text-blue">{currentValue}</span>
              <span className="text-xs font-normal text-black-tint-40">
                {" "}
                / {maxValue}
                {shouldShowPercent ? "%" : undefined}
              </span>
            </div>
          </Tooltip>
        )}
      </div>
      <meter className="h-1 w-full" id={id} max={maxValue} value={currentValue} />
    </div>
  );
};

const getTotalValue = (
  course: CourseType | null,
  prepworkAvgGrade: number | undefined,
  activitiesAvgGrade: number | undefined,
  assignmentsAvgGrade: number | undefined,
) => {
  if (!course) return undefined;
  // Can't calculate total if any of the *relevant* categories are undefined...
  // but if a category has a percentage of 0, then it's not relevant.
  if (
    (prepworkAvgGrade === undefined && course.course_grade_prepwork_percentage > 0) ||
    (activitiesAvgGrade === undefined && course.course_grade_activities_percentage > 0) ||
    (assignmentsAvgGrade === undefined && course.course_grade_assignments_percentage > 0)
  ) {
    return undefined;
  }

  const prepworkValue = (prepworkAvgGrade ?? 0) * course.course_grade_prepwork_percentage;
  const activitiesValue = (activitiesAvgGrade ?? 0) * course.course_grade_activities_percentage;
  const assignmentsValue = (assignmentsAvgGrade ?? 0) * course.course_grade_assignments_percentage;

  return round(prepworkValue, 1) + round(activitiesValue, 1) + round(assignmentsValue, 1);
};

const getSingleTotalBucket = (course: CourseType | null): GradeCategory | undefined => {
  if (!course) return undefined;
  if (course.course_grade_prepwork_percentage === 100) {
    return "prepwork";
  }
  if (course.course_grade_activities_percentage === 100) {
    return "activities";
  }
  if (course.course_grade_assignments_percentage === 100) {
    return "assignments";
  }
  return undefined;
};

type CourseGradeCardProps = {
  /**
   * If `userId` is specified, this component will show the grade for the given user ID;
   * otherwise, it will show the average grade for the entire class.
   * Only instructors can specify this prop; for students, it is ignored.
   */
  userId?: string | null;
};

export const CourseGradeCard: FC<CourseGradeCardProps> = ({ userId: userIdProp = null }) => {
  const dispatch = useAppDispatch();
  const course = useAppSelector(selectCurrentCourse);
  const currentUserId = useAppSelector(selectCurrentUserId);
  const currentOrg = useAppSelector(selectCurrentOrg);
  const { canAuthorCourse } = useCapabilities();

  // If the current user is a student, they are not allowed to view the grade for any other students.
  // Therefore, if the `userId` prop is not provided, we use the current user ID.
  // Instructors are allowed to view the grades for all students, so they can pass a specific `userId`.
  const userId = (canAuthorCourse ? userIdProp : currentUserId) || null;
  const user = useAppSelector((s) => selectUserById(s, userId));
  const courseUserId = useAppSelector((s) => selectCourseUserIdByUserId(s, userId));

  const prepworkAvgGrade = useAppSelector((s) =>
    selectAverageCourseGradeForGradeCategory(s, "prepwork", courseUserId),
  );
  const activitiesAvgGrade = useAppSelector((s) =>
    selectAverageCourseGradeForGradeCategory(s, "activities", courseUserId),
  );
  const assignmentsAvgGrade = useAppSelector((s) =>
    selectAverageCourseGradeForGradeCategory(s, "assignments", courseUserId),
  );
  const totalValue = getTotalValue(
    course,
    prepworkAvgGrade,
    activitiesAvgGrade,
    assignmentsAvgGrade,
  );
  const singleTotalBucket = getSingleTotalBucket(course);
  const isMdOrLarger = useIsMdOrLarger();

  const courseScoreDisplayFormat = currentOrg?.course_score_display_format;
  const orgRubric = currentOrg?.rubric || [];

  const maxCourseScoreForDisplay =
    courseScoreDisplayFormat === CourseScoreDisplayFormat.Percent
      ? 100
      : courseScoreDisplayFormat === CourseScoreDisplayFormat.Scale_4
        ? 4.0
        : 5.0;

  const courseScoreForDisplay =
    totalValue === undefined
      ? undefined
      : courseScoreDisplayFormat === CourseScoreDisplayFormat.Percent
        ? totalValue
        : calculateRubricScore(orgRubric, totalValue);

  // We should only show the values if the course grades are published,
  // or if the user can author the course (in which case they can see the unpublished values).
  const shouldShowValues = !!course?.course_grade_published_at || canAuthorCourse;

  const openGradeSettings = () => {
    dispatch(viewStateActions.setCourseSettingsTabIndex(COURSE_SETTINGS_GRADE_TAB_INDEX));
  };

  return (
    <div className="course-grade-card flex w-full flex-col gap-3 rounded-xl bg-blue-tint-95 p-6 pt-5">
      <div className="flex items-center justify-between gap-2">
        <IconText
          className="min-w-1 text-lg font-semibold text-blue"
          iconEnd={
            totalValue === undefined ? (
              <Tooltip text={t("assessment_dashboard.tooltip.course_score")}>
                <CircleInfoIcon />
              </Tooltip>
            ) : undefined
          }
          text={
            <div className="shrink truncate">
              {isMdOrLarger
                ? canAuthorCourse
                  ? user
                    ? t("assessment_dashboard.grades.title_name", {
                        name: getUserFullName(user),
                      })
                    : totalValue
                      ? t("assessment_dashboard.grades.title_average")
                      : t("assessment_dashboard.grades.title_calculations")
                  : t("assessment_dashboard.grades.title_own")
                : t("assessment_dashboard.grades.title_short")}
            </div>
          }
        />
        {canAuthorCourse && (
          <Button className="shrink-0 border-0" kind="white" size="s" onClick={openGradeSettings}>
            <IconText
              iconStart={<GearSettingsIcon />}
              text={
                isMdOrLarger
                  ? t("assessment_dashboard.grades.settings")
                  : t("assessment_dashboard.grades.settings_short")
              }
            />
          </Button>
        )}
      </div>
      <div className="flex flex-col gap-4">
        <CourseGradeCardSection
          currentValue={
            shouldShowValues && typeof courseScoreForDisplay === "number"
              ? round(
                  courseScoreForDisplay,
                  courseScoreDisplayFormat === CourseScoreDisplayFormat.Percent ? 1 : 2,
                )
              : undefined
          }
          maxValue={maxCourseScoreForDisplay}
          title={
            singleTotalBucket
              ? `${t("assessment_dashboard.grades.total")}: ${t(`assessment_dashboard.grades.${singleTotalBucket}`)}`
              : t("assessment_dashboard.grades.total")
          }
          userId={userId}
          isCourseScore
        />
        {!singleTotalBucket && (
          <div className="flex flex-col gap-4 md:flex-row md:gap-6">
            {(
              [
                ["prepwork", prepworkAvgGrade],
                ["activities", activitiesAvgGrade],
                ["assignments", assignmentsAvgGrade],
              ] as const
            ).map(([category, avgPoints]) => {
              const maxValue = course?.[`course_grade_${category}_percentage`] ?? 0;
              return maxValue > 0 ? (
                <div
                  className="min-w-1 shrink"
                  key={category}
                  style={isMdOrLarger ? { width: `${Math.max(maxValue, 25)}%` } : undefined}
                >
                  <CourseGradeCardSection
                    currentValue={
                      shouldShowValues && typeof avgPoints === "number"
                        ? round(avgPoints * maxValue, 1)
                        : undefined
                    }
                    maxValue={maxValue}
                    title={t(`assessment_dashboard.grades.${category}`)}
                    userId={userId}
                  />
                </div>
              ) : null;
            })}
          </div>
        )}
      </div>
    </div>
  );
};
